import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@etnas/design-system';
import { isApiError, schedulingApi } from '@/api';
import type { RoomingClaim } from '@/api/modules/scheduling';
import { useMe } from '@/app/auth';
import { usePatientSlots } from '@/app/patientSlots';
import { ActionError, QueryView, StateBlock } from '@/components/States';
import { fmtTime } from '@/lib/format';
import { qk } from '@/lib/queryKeys';

/** Arrival details only: an explicit, audited claim must succeed before entering the chart. */
export function RoomingClaims({ patientId }: { patientId?: string }) {
  const me = useMe(), locationId = me.context.locationId;
  const query = useQuery({ queryKey: ['rooming-claims', locationId, patientId],
    queryFn: () => schedulingApi.roomingClaims(locationId!, patientId), enabled: !!locationId, refetchInterval: 60_000 });
  if (!locationId) return <StateBlock kind="validation" title="Choose your location" body="Select the visit location to start rooming." />;
  return <section className="ex-panel" aria-label="Start rooming">
    <div className="ex-panel-body ex-stack">
      <h2>Start rooming</h2>
      <p>Claim the visit you are rooming to open the patient’s chart.</p>
      <QueryView q={query} what="visits waiting for rooming" isEmpty={data => data.items.length === 0}
        empty={{ title: 'No visits waiting for a claim', body: 'Arrived visits at your location appear here.' }}>
        {data => <ul className="ex-stack">{data.items.map(visit => <li key={visit.id}>
          <div>{visit.patient.display} · DOB {visit.patient.dob} · {fmtTime(visit.start)} · {visit.providerName}</div>
          <ClaimRoomingButton visit={visit} onConflict={() => query.refetch()} />
        </li>)}</ul>}
      </QueryView>
    </div>
  </section>;
}

function ClaimRoomingButton({ visit, onConflict }: { visit: RoomingClaim; onConflict: () => unknown }) {
  const qc = useQueryClient(), { openPatient } = usePatientSlots();
  const [key, setKey] = useState(() => crypto.randomUUID());
  const claim = useMutation({ mutationFn: () => schedulingApi.claimRooming(visit, key),
    onSuccess: async () => {
      await Promise.all([qc.invalidateQueries({ queryKey: qk.patient(visit.patient.id) }), qc.invalidateQueries({ queryKey: ['appointments'] }), qc.invalidateQueries({ queryKey: ['rooming-claims'] })]);
      openPatient(visit.patient, `/chart/${visit.patient.id}/rooming`);
  }, onError: error => { if (isApiError(error, 'ETNAS_VERSION_CONFLICT') || isApiError(error, 'ETNAS_ILLEGAL_TRANSITION')) { setKey(crypto.randomUUID()); onConflict(); } },
  });
  return <>
    <Button icon="vitals" disabled={claim.isPending} onClick={() => claim.mutate()} aria-label={`Start rooming for ${visit.patient.display}`}>Start rooming</Button>
    {claim.error ? <ActionError error={claim.error} /> : null}
  </>;
}
